import { useState, useCallback } from "react";
import userService from "../services/userService";
import roleService from "../services/roleService";

export default function useUser() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const formatUser = (u) => {
    if (!u) return null;
    return {
      id: u.id,
      name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—",
      email: u.email || "—",
      phone: u.phoneNumber || "—",
      role: u.roles?.[0]?.name || "—",
      type: u.type || u.userType || u.raw?.type || "—",
      status: u.employmentStatus || "ACTIVE",
      raw: u,
    };
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      const data = Array.isArray(response.data) ? response.data : response;
      setUsers(data.map(formatUser));
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserById = async (id) => {
    try {
      const res = await userService.getUserById(id);
      return res.data || res;
    } catch (err) {
      console.error(`Failed to fetch user ${id}:`, err);
      throw err;
    }
  };

  const createUser = async (payload) => {
    try {
      const res = await userService.createUser(payload);
      const created = res.data || res;
      setUsers((prev) => [...prev, formatUser(created)]);
      return formatUser(created);
    } catch (err) {
      console.error("Failed to create user:", err);
      throw err;
    }
  };

  const updateUser = async (id, payload) => {
    try {
      const res = await userService.updateUser(id, payload);
      const updated = res.data || res;
      setUsers((prev) => prev.map((u) => (u.id === id ? formatUser(updated) : u)));
      return formatUser(updated);
    } catch (err) {
      console.error(`Failed to update user ${id}:`, err);
      throw err;
    }
  };

  const deleteUser = async (id) => {
    try {
      await userService.deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      return true;
    } catch (err) {
      console.error(`Failed to delete user ${id}:`, err);
      throw err;
    }
  };

  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      const response = await roleService.getAllRoles();
      const data = Array.isArray(response.data) ? response.data : response;
      setRoles(
        data
          .filter((role) => !role.internalRole)
          .map((role) => ({
            id: role.id,
            name: role.name,
            permissions: role.permissions || [],
          }))
      );
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  return {
    users,
    loading,
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
    roles,
    rolesLoading,
    fetchRoles,
  };
}
